import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Phone, Mail, MessageCircle, Facebook, User, ExternalLink, Hand, Undo2,
  Trash2, CalendarClock, Stethoscope,
  FileText, PhoneCall, PhoneOutgoing, PhoneIncoming, Circle, ShieldAlert
} from 'lucide-react'
import { apiGet, apiDelete } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import { apptStatusInfo } from '../../data/appointmentStatuses.js'
import { useApptTypes } from '../../components/ApptType.jsx'
import { lockState, LOCK_META } from '../../lib/recallLock.js'
import { canEditClinical } from '../../lib/role.js'
import { asList, fmtDur, callTs, tsOf } from '../../lib/datetime.js'
// useSection (a data hook), Section (a heading) and CopyText have no kit
// equivalent yet; the rest of DrawerPrimitives is replaced by kit parts below.
import { useSection, Section, CopyText } from '../ui/DrawerPrimitives.jsx'
import {
  Button, ConfirmDialog, DataTable, Drawer, DrawerFooter, EmptyState, PatientLink, Tabs,
  fmtDate, fmtMoney, useTableState, useToast,
} from '../../kit'
import RecallNotesSection from './RecallNotesSection.jsx'
import ImplantReviewSection from './ImplantReviewSection.jsx'
import { CALL_ICON, CALL_LABEL, callEventConcerns } from '../../lib/phone.js'
import { buildChartIndex } from '../../lib/chart-helpers.js'
import { treatmentSummary } from '../../lib/treatmentSummary.js'
import { useXanoRealtime } from '../../lib/realtime.js'

/**
 * RecallPopup — the drawer that opens when staff pick a patient off the recall
 * worklist. Mirrors the Call page drawer: one place to SEE everything about the
 * patient (contact, appointments, treatments, charts, call log) and DO the recall
 * (log a call-back note, claim/release the lock so coworkers see it live).
 *
 * The patient is LOCKED on open (the parent claims before mounting this). This
 * component shows the live lock state — kept fresh by the shared `recall_update`
 * realtime channel — and exposes ปล่อย / รับต่อ (release / takeover). "One patient
 * at a time" + auto-release-of-the-previous is handled by the parent.
 *
 * Props:
 *   patient       the worklist row (id, name, HN, status, contact…)
 *   me            current user ({ employee_id })
 *   onClose       close the drawer (parent releases the lock)
 *   onLockAction  (request_type:'claim'|'release') => Promise — parent runs the
 *                 proxy call + tracks the held patient; we refetch after it settles.
 *                 Omitted → the lock bar is read-only: who holds the patient, no
 *                 buttons, and no bar at all while nobody does (the implant รีวิว
 *                 tab — reviewers never claim a call).
 *   onChanged     () => void — tell the list to refetch (note CRUD changed counts)
 *   refreshKey    bumps when the parent's realtime refetch fires → we re-read the
 *                 patient's live lock. The parent owns the `recall_update`
 *                 subscription and hands the refetch down, so the popup and the
 *                 list reload off the same event. (Built that way to dodge the
 *                 old channel collision; lib/realtime.js now shares one channel
 *                 per name, so a second subscriber would also be safe.)
 */
const pid = (p) => p?.patient_id ?? p?.id ?? null
const fullname = (p) => String(p?.fullname || [p?.name, p?.surname].filter(Boolean).join(' ') || '').trim()

// The proxy answers 409 with a Thai `detail` ("ลบไม่ได้ — ผู้ป่วยรายนี้ยังมี: …");
// apiDelete wraps it as `HTTP 409: {json}`, so dig the sentence back out.
function deleteErrText(e) {
  const msg = String(e?.message || '')
  const i = msg.indexOf('{')
  if (i >= 0) {
    try { const j = JSON.parse(msg.slice(i)); if (j?.detail) return j.detail } catch { /* not json */ }
  }
  return msg || 'ลบผู้ป่วยไม่สำเร็จ'
}

// `notesReadOnly` shows the note history without the add box or edit / delete (the
// implant รีวิว tab). `notePrefix` / `quickCodes` are passed straight through to RecallNotesSection —
// /reports/implant uses them to code the campaign's recall notes. Every existing
// caller omits both and behaves exactly as before.
// `fullPageTab` picks which patient tab "เปิดหน้าเต็ม" opens: /reports/implant passes
// 'treatments'; omitted, it stays /patient/:id (the info tab) as before.
// `implantReview` (a list letter) adds the รีวิวก่อนโทร section — steps 1-2 of the implant
// process — above the notes; `onReviewed` fires after each review is saved. Only
// /reports/implant passes them; every other caller renders exactly as before.
// The section reads the round, the parking and the W flag off `patient` itself — the
// api 678 list row (2026-10-29) — so nothing else needs passing.
export default function RecallPopup({ patient, me, onClose, onLockAction, onChanged, onDeleted, refreshKey, notePrefix = '', quickCodes = [], notesReadOnly = false, fullPageTab = '', implantReview = '', onReviewed }) {
  const patientId = pid(patient)
  const meId = me?.employee_id

  // full patient (contact + live lock fields) — the row may be a lean worklist row
  const [full, setFull] = useState(null)
  const refetchFull = useCallback(() => {
    if (patientId == null) return
    apiGet(`/api/patients/${patientId}`).then(setFull).catch(() => {})
  }, [patientId])
  // re-read on open AND whenever the parent's realtime refetch bumps refreshKey
  useEffect(() => { setFull(null); refetchFull() }, [patientId, refreshKey, refetchFull])

  // Esc / backdrop / ✕ close are the kit Drawer's (Esc reaches only the top-most
  // overlay, so a ConfirmDialog over the drawer closes itself first).

  const lockSubject = full || patient
  const lock = lockState(lockSubject, meId)

  const [busy, setBusy] = useState('')
  async function doLock(request_type) {
    setBusy(request_type)
    try { await onLockAction?.(request_type) } finally { setBusy(''); refetchFull() }
  }

  // ── ลบผู้ป่วย (empty patients only) ────────────────────────────────────────
  // `counts` comes from RecordsTabs below — the นัดหมาย/การรักษา lists it already
  // fetched. The zone only appears once both have loaded AND both are zero, so a
  // patient with history never shows a delete affordance at all. Billing is NOT
  // fetched here; the proxy re-checks all three and answers 409 with a Thai reason.
  const [counts, setCounts] = useState(null)
  const [del, setDel] = useState({ armed: false, busy: false, err: '' })
  const toast = useToast()
  const canDelete = canEditClinical(me) && counts != null && counts.appt === 0 && counts.tx === 0
  useEffect(() => { setDel({ armed: false, busy: false, err: '' }) }, [patientId])

  async function confirmDelete() {
    if (patientId == null) return
    setDel((d) => ({ ...d, busy: true, err: '' }))
    try {
      await apiDelete(`/api/patients/${patientId}`)
      setDel({ armed: false, busy: false, err: '' })
      toast.success('ลบผู้ป่วยแล้ว')
      onDeleted?.(patientId)          // parent drops the row, closes the drawer
    } catch (e) {
      const err = deleteErrText(e)
      setDel({ armed: false, busy: false, err })
      toast.error('ลบผู้ป่วยไม่สำเร็จ', { detail: err })
    }
  }

  const contact = full?.contact || patient?.contact || {}
  const person = full?.contact_person || patient?.contact_person || {}
  const st = full?.status || patient?.status || ''
  const tone = STATUS_TONES[statusTone(st)] || STATUS_TONES.none

  const name = fullname(patient)
  const hn = full?.HN || patient?.HN
  return (
    <>
    <Drawer
      open
      onClose={onClose}
      title={(
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[15px] font-semibold shrink-0">{nameInitial(name || '?')}</span>
          <span className="truncate">{name || '—'}</span>
        </span>
      )}
      subtitle={(
        <span className="flex items-center gap-2 flex-wrap text-[12px]">
          <span className="text-slate-400">ติดตามคนไข้ (Recall)</span>
          {hn && <span className="text-slate-500 tabular-nums">HN {hn}</span>}
          {st && <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${tone.pill}`}><span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{st}</span>}
          {patientId != null && (
            <PatientLink patientId={patientId} context="followup" tab={fullPageTab || undefined} className="inline-flex items-center gap-0.5 text-[12px]">
              <ExternalLink size={12} className="inline" /> เปิดหน้าเต็ม
            </PatientLink>
          )}
        </span>
      )}
      busy={del.busy}
      footer={canDelete ? (
        <DrawerFooter
          hideCancel
          danger={<Button variant="danger-soft" icon={Trash2} onClick={() => setDel({ armed: true, busy: false, err: '' })}>ลบผู้ป่วยรายนี้</Button>}
          status={del.err || 'คนไข้รายนี้ไม่มีนัดหมายและการรักษา · การลบไม่สามารถย้อนกลับได้'}
          statusTone={del.err ? 'error' : 'muted'}
        />
      ) : null}
    >
        {/* lock line — who is calling this patient right now */}
        <div className="-mt-1 mb-4">
          {/* read-only and nobody holds the patient → nothing worth saying */}
          {(onLockAction || lock.status !== 'free') && (
            <LockBar lock={lock} full={lockSubject} busy={busy} readOnly={!onLockAction} onClaim={() => doLock('claim')} onRelease={() => doLock('release')} />
          )}
        </div>

        <div className="space-y-5">
          <ContactSection contact={contact} person={person} />
          {implantReview && (
            <ImplantReviewSection patientId={patientId} list={implantReview} row={patient} onChanged={onReviewed} refreshKey={refreshKey} />
          )}
          <RecallNotesSection patientId={patientId} meId={meId} onChanged={onChanged}
            notePrefix={notePrefix} quickCodes={quickCodes} readOnly={notesReadOnly} />
          <RecordsTabs patientId={patientId} onCounts={setCounts} />
        </div>
    </Drawer>

      {/* Sibling AFTER the drawer → the top-most overlay, not confined by its panel. */}
      {del.armed && (
        <ConfirmDialog
          tone="danger"
          title="ลบผู้ป่วยรายนี้ถาวร?"
          message={`“${name || `#${patientId}`}”${hn ? ` (HN ${hn})` : ''} จะถูกลบออกจากระบบถาวร — ย้อนกลับไม่ได้`}
          confirmLabel="ลบถาวร"
          busy={del.busy}
          onConfirm={confirmDelete}
          onCancel={() => setDel({ armed: false, busy: false, err: del.err })}
        />
      )}
    </>
  )
}

/* ── lock bar ─────────────────────────────────────────────────────────────── */
function LockBar({ lock, full, busy, readOnly, onClaim, onRelease }) {
  const m = LOCK_META[lock.status] || LOCK_META.free
  const byCode = full?.employee_lock_by?.code || full?.lock_by_employee?.code || (lock.by != null ? `#${lock.by}` : '')
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-xl border border-slate-200 bg-white px-3 py-2">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${m.cls}`}>
        <Circle size={7} className={`${m.dot.replace('bg-', 'text-')} fill-current`} />{m.label}
      </span>
      {lock.byOther && byCode && <span className="text-[11.5px] text-rose-600 inline-flex items-center gap-1"><ShieldAlert size={12} />โดย {byCode}</span>}
      {!readOnly && <span className="ml-auto flex items-center gap-1.5">
        {lock.mine ? (
          <Button size="sm" variant="secondary" icon={Undo2} loading={busy === 'release'} onClick={onRelease}>ปล่อย</Button>
        ) : lock.byOther ? (
          <Button size="sm" variant="soft" icon={Hand} loading={busy === 'claim'} onClick={onClaim}>รับต่อ</Button>
        ) : (
          <Button size="sm" icon={Hand} loading={busy === 'claim'} onClick={onClaim}>รับเรื่อง</Button>
        )}
      </span>}
    </div>
  )
}

/* ── contact ──────────────────────────────────────────────────────────────── */
function ContactSection({ contact, person }) {
  const rows = [
    { Icon: Phone, label: 'เบอร์โทร', value: contact.phone, copy: true },
    { Icon: Mail, label: 'อีเมล', value: contact.email },
    { Icon: MessageCircle, label: 'LINE', value: contact.line },
    { Icon: Facebook, label: 'Facebook', value: contact.facebook, href: /^https?:/.test(contact.facebook || '') ? contact.facebook : undefined },
  ].filter((r) => r.value)
  const hasPerson = person && (person.name || person.phone)
  return (
    <Section title="ข้อมูลติดต่อ" icon={User}>
      {rows.length === 0 && !hasPerson ? (
        <EmptyState variant="compact" title="ไม่มีข้อมูลติดต่อ" />
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-2 text-[13px]">
              <r.Icon size={14} className="text-slate-400 shrink-0" />
              <span className="text-slate-400 w-16 shrink-0">{r.label}</span>
              {r.href
                ? <a href={r.href} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline truncate">{r.value}</a>
                : r.copy
                  ? <CopyText text={r.value} className="text-slate-700 tabular-nums" />
                  : <span className="text-slate-700 truncate">{r.value}</span>}
            </div>
          ))}
          {hasPerson && (
            <div className="flex items-center gap-2 text-[13px] pt-1 mt-1 border-t border-slate-50">
              <User size={14} className="text-slate-400 shrink-0" />
              <span className="text-slate-400 w-16 shrink-0">ผู้ติดต่อ</span>
              <span className="text-slate-700 truncate">{person.name || '—'}{person.relationship ? ` (${person.relationship})` : ''}{person.phone ? ` · ${person.phone}` : ''}</span>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

/* ── recall notes (this patient's call-back log) — add / edit / delete ─────── */
/* ── records tabs: นัดหมาย / การรักษา / ประวัติการโทร ──────────────────────────
 * One tabbed switcher instead of three stacked lists. Each list paginates
 * client-side (10/page, latest-first). Charts are still fetched — not shown as
 * their own tab, but used to flag which treatments have a chart. */

function RecordsTabs({ patientId, onCounts }) {
  const [tab, setTab] = useState('appt')
  const { idToLabel } = useApptTypes()
  const appts = useSection('/api/appointments', { patient_id: patientId, type: 'patient' }, patientId)
  const txs = useSection('/api/treatments', { patient_id: patientId }, patientId)
  // ประวัติการโทร stays live while the drawer is open. A call reaches the `call` table
  // ~20 s after hang-up (median; 90% within 1 min, 99% within ~2 min — measured
  // 2026-10-29 on 7 days of outbound calls), and its insert trigger (trigger 15) links
  // the patient by number and broadcasts `call_update` {type:'new', id, patient_id} — the
  // patient_id since 2026-10-30, so the drawer re-reads only for ITS patient (it used to
  // re-read on every call in the clinic, ~4 a minute at peak). An announcement with no
  // patient_id at all (an older trigger, another source) still re-reads; 0 / '' is a call
  // that matched no patient and can never be in this list. Debounced, and quiet
  // (useSection's tick keeps the rows on screen). Coming back to the window (from the
  // phone) re-reads too, in case the socket dropped.
  const [phoneTick, setPhoneTick] = useState(0)
  const phones = useSection('/api/phone', { patient_id: patientId }, patientId, phoneTick)
  const phoneTimer = useRef(null)
  const bumpPhones = useCallback(() => {
    clearTimeout(phoneTimer.current)
    phoneTimer.current = setTimeout(() => setPhoneTick((n) => n + 1), 1500)
  }, [])
  useXanoRealtime('call_update', (msg) => { if (callEventConcerns(msg, patientId)) bumpPhones() })
  useEffect(() => {
    const onBack = () => { if (document.visibilityState === 'visible') bumpPhones() }
    window.addEventListener('focus', onBack)
    document.addEventListener('visibilitychange', onBack)
    return () => {
      clearTimeout(phoneTimer.current)
      window.removeEventListener('focus', onBack)
      document.removeEventListener('visibilitychange', onBack)
    }
  }, [bumpPhones])
  const charts = useSection('/api/charts', { patient_id: patientId }, patientId)

  // Report นัดหมาย/การรักษา counts up to the drawer, which uses them to decide whether
  // to offer ลบผู้ป่วย. These two lists are already fetched for the tabs, so the delete
  // gate costs no extra request — and it reads the REAL records, not the worklist row's
  // `*_count` fields (which some tabs omit entirely). Cancelled rows still count: they
  // are history, and the server guard counts them too.
  // A FAILED fetch also lands as `data: []` (see useSection), which would read as
  // "no records" and offer to delete a patient who has plenty — so an errored list
  // reports null, i.e. "unknown", and the delete zone stays hidden.
  useEffect(() => {
    if (appts.data == null || txs.data == null || appts.err || txs.err) { onCounts?.(null); return }
    onCounts?.({
      appt: asList(appts.data).filter((a) => a && a.active !== false).length,
      tx: asList(txs.data).filter((t) => t && t.active !== false).length,
    })
  }, [appts.data, txs.data, appts.err, txs.err, onCounts])

  // each treatment's chart (the richest when Xano holds duplicates) → the violet "chart"
  // flag and the doctor's notes in the treatment summary
  const chartIndex = useMemo(() => buildChartIndex(asList(charts.data)), [charts.data])

  const apptRows = useMemo(() => asList(appts.data).slice().sort((a, b) => tsOf(b.start_date) - tsOf(a.start_date)), [appts.data])
  const txRows = useMemo(() => asList(txs.data).slice().sort((a, b) => tsOf(b.date) - tsOf(a.date)), [txs.data])
  const phoneRows = useMemo(() => asList(phones.data).slice().sort((a, b) => callTs(b) - callTs(a)), [phones.data])

  const TABS = [
    { key: 'appt', label: 'นัดหมาย', icon: CalendarClock, badge: appts.data == null ? undefined : apptRows.length },
    { key: 'tx', label: 'การรักษา', icon: Stethoscope, badge: txs.data == null ? undefined : txRows.length },
    { key: 'phone', label: 'ประวัติการโทร', icon: PhoneCall, badge: phones.data == null ? undefined : phoneRows.length },
  ]

  return (
    <section>
      <Tabs className="mb-3" items={TABS} value={tab} onChange={setTab} />

      {tab === 'appt' && (
        <RecordsTable section={appts} rows={apptRows} empty="ไม่มีนัดหมาย" columns={APPT_COLS(idToLabel)} />
      )}
      {tab === 'tx' && (
        <RecordsTable section={txs} rows={txRows} empty="ไม่มีประวัติการรักษา" columns={TX_COLS(chartIndex)} />
      )}
      {tab === 'phone' && (
        <RecordsTable section={phones} rows={phoneRows} empty="ไม่มีบันทึกการโทร" columns={PHONE_COLS} />
      )}
    </section>
  )
}

/* Read-only record lists — 10 per page, newest first (the caller sorts), paging in
 * the table's footer. A failed fetch shows its error, never an empty list. */
function RecordsTable({ section, rows, columns, empty }) {
  const t = useTableState(rows, columns, { pageSize: 10 })
  return (
    <DataTable
      columns={columns}
      {...t.tableProps}
      rowKey={(r, i) => r.id ?? i}
      valign="top"
      loading={section.data == null && !section.err}
      error={section.err ? `โหลดไม่สำเร็จ: ${section.err}` : undefined}
      empty={{ title: empty }}
    />
  )
}

const APPT_COLS = (idToLabel) => [
  { key: 'date', label: 'วันที่', render: (a) => <span className="tabular-nums whitespace-nowrap">{fmtDate(a.start_date)}</span> },
  { key: 'type', label: 'ประเภท', render: (a) => idToLabel.get(String(a.appointmentlist_id)) || a.title || '—' },
  { key: 'doctor', label: 'หมอ', render: (a) => a.doctor?.code || a.doctor?.name || (a.doctor_id ? `#${a.doctor_id}` : '—') },
  {
    key: 'status', label: 'สถานะ',
    render: (a) => {
      const info = apptStatusInfo(a.status)
      return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] ring-1 whitespace-nowrap ${info.pill || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>{info.label}</span>
    },
  },
]

/* One line of the treatment summary: a muted label, the text clamped to two lines,
 * the whole of it in the tooltip. */
function TxLine({ label, text }) {
  return (
    <span className="mt-0.5 text-[12px] leading-snug text-slate-500 line-clamp-2 break-words" title={text}>
      {label && <span className="mr-1 font-medium text-slate-400">{label}</span>}{text}
    </span>
  )
}

/* รายการ = what happened at the visit (Ram, 2026-10-30): the type, what was billed, then
 * the doctor's CC / Tx / N/V and any note — lib/treatmentSummary.js. A visit with no
 * type leads with its billed items instead of "—". */
const TX_COLS = (chartIndex) => [
  { key: 'date', label: 'วันที่', render: (t) => <span className="tabular-nums whitespace-nowrap">{fmtDate(t.date)}</span> },
  {
    key: 'name', label: 'รายการ',
    render: (t) => {
      const chart = chartIndex.findFor(t)
      const s = treatmentSummary(t, chart)
      const type = t.title || t.treatment_name || ''
      const billed = s.items.join(' · ')
      return (
        <span className="block min-w-0">
          {/* wraps (2 lines) rather than truncates: a no-wrap line would widen the table
              past the drawer when a visit with no type leads with a long bill */}
          <span className="line-clamp-2 break-words text-slate-800" title={type || billed || undefined}>
            {type || billed || '—'}
            {chart && <span title="มี chart" className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-violet-50 text-violet-700 ring-1 ring-violet-200 px-1 py-0.5 align-middle text-[10px]"><FileText size={10} />chart</span>}
          </span>
          {type && billed && <TxLine text={billed} />}
          {s.cc && <TxLine label="CC" text={s.cc} />}
          {s.exam && <TxLine label="Tx" text={s.exam} />}
          {s.nv && <TxLine label="N/V" text={s.nv} />}
          {s.notes.length > 0 && <TxLine label="หมายเหตุ" text={s.notes.join(' · ')} />}
        </span>
      )
    },
  },
  { key: 'doctor', label: 'หมอ', render: (t) => <span className="whitespace-nowrap">{t.doctor?.code || t.doctor?.name || '—'}</span> },
  {
    key: 'total', label: 'ยอด (รวม)', align: 'right',
    render: (t) => { const total = treatmentTotal(t); return <span className="font-medium text-slate-800">{total ? fmtMoney(total) : '—'}</span> },
  },
]

const PHONE_COLS = [
  { key: 'date', label: 'วันที่', render: (c) => <span className="tabular-nums whitespace-nowrap">{fmtDate(callTs(c))}</span> },
  {
    key: 'dir', label: 'ทิศทาง',
    render: (c) => {
      const Dir = c.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming
      return <span className="inline-flex items-center gap-1 text-slate-500 whitespace-nowrap"><Dir size={12} />{c.direction === 'outbound' ? 'ออก' : 'เข้า'}</span>
    },
  },
  {
    key: 'result', label: 'ผล',
    render: (c) => {
      const Ico = CALL_ICON[c.call_result] || Phone
      return <span className="inline-flex items-center gap-1 text-slate-600 whitespace-nowrap"><Ico size={12} />{CALL_LABEL[c.call_result] || c.call_result || '—'}</span>
    },
  },
  { key: 'dur', label: 'ระยะเวลา', align: 'right', render: (c) => fmtDur(c.call_duration) },
]

// Σ (price × unit) across the treatment's billing lines = the รวม column
// (what the patient pays; called เก็บเอง before 2026-08-13).
// Falls back to a total field if the row doesn't carry billing[].
function treatmentTotal(t) {
  if (Array.isArray(t?.billing) && t.billing.length) {
    return t.billing.reduce((s, b) => {
      const px = b.priceXunit ?? (Number(b.price || 0) * Number(b.N ?? b.unit ?? 1))
      return s + (Number(px) || 0)
    }, 0)
  }
  return Number(t?.total_price ?? t?.priceActualTotal ?? t?.total ?? 0) || 0
}
